// namat#30: a user belongs to at most ONE support group per organization
// (groups in different organizations are fine). The guard
// (groupMembershipGuard.ts, via assertMembershipAllowed) refuses a second
// one with SubgroupConflictError; this is the copy every join/add path shows
// for it, so the beneficiary and the manager read the same rule everywhere.

export type ConflictAudience =
  /** The user is joining by themselves. */
  | 'self'
  /** A manager/editor is adding someone else. */
  | 'manager';

const quoted = (name: string | undefined, rtl: boolean) =>
  name ? (rtl ? ` («${name}»)` : ` ("${name}")`) : '';

/** The refusal: shown when the join/add cannot go ahead. */
export function membershipConflictMessage(
  audience: ConflictAudience,
  rtl: boolean,
  existingGroupName?: string,
): string {
  if (audience === 'self') {
    return rtl
      ? `أنت عضو في مجموعة أخرى في هذه المنظمة${quoted(existingGroupName, rtl)}. لا يمكن الانضمام إلى أكثر من مجموعة في المنظمة نفسها.`
      : `You are already in another group of this organization${quoted(existingGroupName, rtl)}. You can only belong to one group per organization.`;
  }
  return rtl
    ? `المستفيد عضو في مجموعة أخرى في هذه المنظمة${quoted(existingGroupName, rtl)} — انقله بدل الإضافة.`
    : `This beneficiary is already in another group of this organization${quoted(existingGroupName, rtl)} — move them instead of adding.`;
}

/** The move offer a manager confirms (destructive: the old membership is removed). */
export function membershipMoveConfirm(rtl: boolean, fromGroupName: string, toGroupName: string): string {
  return rtl
    ? `المستفيد عضو في مجموعة أخرى في هذه المنظمة. سيُنقل من «${fromGroupName}» إلى «${toGroupName}». هل تريد المتابعة؟`
    : `This beneficiary is already in another group of this organization. Move them from "${fromGroupName}" to "${toGroupName}"?`;
}

/** Sections are conceptual and never joined directly. */
export function sectionNotJoinableMessage(rtl: boolean): string {
  return rtl ? 'لا يمكن الانضمام إلى الأقسام مباشرة' : 'Sections cannot be joined directly';
}
